import upperFirst from 'lodash/upperFirst';
import { inject, observer } from 'mobx-react';
import moment from 'moment';
import React from 'react';
import { Alert, Button, Modal, Toast, ToastContainer } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import DeployMode from '../../../models/deploy/DeployMode';
import {
  getModelFilePath,
  ModelFolder,
  transformModelName,
} from '../../../models/ModelItem';
import { PROFILES_PERMISSIONS } from '../../../permissions';
import Store from '../../../store';
import {
  IS_LLTB,
  MODEL_TYPES_NAMES,
  TENANT_CONFIG_REPO,
  TENANT_CONFIG_DEFAULT_BRANCH,
} from '../../../utils/constants';
import checkPermissions from '../../components/CheckPermissions';
import BehavioralTab from '../../components/deploy/BehavioralTab';
import MultiEngagementTab from '../../components/deploy/MultiEngagementTab';
import MultiFitTab from '../../components/deploy/MultiFitTab';
import StandardFitTab from '../../components/deploy/StandardFitTab';
import Error from '../../components/error/Error';
import Spinner from '../../components/Spinner';
import launchAirflowDag from '../../../utils/launch_airflow_dag';

interface DeployPageProps {
  store?: Store;
}

type DeployPageState = {
  selectedDeployMode: DeployMode;
  selectedFolders: ModelFolder[];
  loading: boolean;
  // Feedback for "Estimate Deployment Impact" (RGI-2319): the launch used to fire
  // and close the modal with no spinner, confirmation, or error, so users re-clicked
  // and triggered duplicate DAG runs.
  impactLaunching: boolean;
  impactError: string | null;
  impactStarted: boolean;
};

export default inject('store')(
  observer(
    class DeployPage extends React.Component<DeployPageProps, DeployPageState> {
      interval: number;

      constructor(props: DeployPageProps) {
        super(props);

        this.state = {
          selectedDeployMode: 'standard',
          selectedFolders: [],
          loading: true,
          impactLaunching: false,
          impactError: null,
          impactStarted: false,
        };

        this.handleDeployModel = this.handleDeployModel.bind(this);
        this.openDeployPopup = this.openDeployPopup.bind(this);
        this.toggleShowDeployModal = this.toggleShowDeployModal.bind(this);
        this.openImpactPopup = this.openImpactPopup.bind(this);
        this.toggleShowImpactModal = this.toggleShowImpactModal.bind(this);
        this.startImpactAnalysis = this.startImpactAnalysis.bind(this);
      }

      async componentDidMount() {
        await this.props.store.loadValidationData({ isProcessing: true });

        // Lead grade performances are not computed yet
        if (
          IS_LLTB(this.props.store.activeModel.type) &&
          !this.props.store.leadGradePage.performances
        ) {
          await this.props.store.getLeadGradeConfiguration();
          await this.props.store.calculateLeadGradePerformance({
            isProcessing: true,
          });
        }

        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.props.store.updateDeployStatus().then(() => {
          if (this.props.store.deployPage.isDeploying) {
            this.interval = window.setInterval(async () => {
              await this.props.store.updateDeployStatus();
              if (!this.props.store.deployPage.isDeploying) {
                clearInterval(this.interval);
              }
            }, 2000);
          }
        });
        this.setState({ loading: false });
      }

      UNSAFE_componentWillUnmount() {
        clearInterval(this.interval);
      }

      async handleDeployModel() {
        const { selectedFolders, selectedDeployMode } = this.state;
        await this.props.store.deployModel(selectedFolders, selectedDeployMode);
      }

      async openDeployPopup(deployMode: DeployMode, folders: ModelFolder[]) {
        this.setState({
          selectedDeployMode: deployMode,
          selectedFolders: folders,
        });
        this.props.store.deployPage.loadingDeployMode = deployMode;

        await Promise.all(
          folders.map((folder) =>
            this.props.store.getLastCommit(folder, deployMode)
          )
        );

        this.props.store.deployPage.loadingDeployMode = null;
        this.toggleShowDeployModal();
      }

      toggleShowDeployModal() {
        this.props.store.deployPage.deployPopupIsShown =
          !this.props.store.deployPage.deployPopupIsShown;
      }

      async openImpactPopup() {
        this.toggleShowImpactModal();
      }

      toggleShowImpactModal() {
        this.props.store.deployPage.impactPopupIsShown =
          !this.props.store.deployPage.impactPopupIsShown;
      }

      async startImpactAnalysis() {
        if (this.state.impactLaunching) return;
        const { activeModel, tenant, email } = this.props.store;
        this.setState({ impactLaunching: true, impactError: null });
        try {
          await launchAirflowDag({
            tenant,
            dagName: 'deployment_impact_analysis',
            dagConf: {
              model_id: activeModel.modelId,
              model_type: activeModel.type,
              TENANT: tenant,
              email,
            },
          });
          // Close the modal and confirm via a toast, so the user knows it started
          // and does not re-trigger a second run.
          this.props.store.deployPage.impactPopupIsShown = false;
          this.setState({ impactLaunching: false, impactStarted: true });
        } catch (error) {
          // Keep the modal open and surface the failure instead of swallowing it.
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const err = error as any;
          const message =
            err.response?.data?.message ||
            err.message ||
            'Failed to start the analysis. Please try again.';
          this.setState({ impactLaunching: false, impactError: message });
        }
      }

      render() {
        const { activeModel, validationPage, deployPage, isSuperKudu, tenant } =
          this.props.store;
        const {
          deploymentError,
          deployPopupIsShown,
          lastCommits,
          isDeploying,
          impactPopupIsShown,
        } = deployPage;
        const {
          validation: { error: validationError },
        } = validationPage;
        const { selectedDeployMode, selectedFolders } = this.state;

        const transformedModelName = transformModelName(activeModel.name);

        const unconventionalCommits = Object.entries(lastCommits)
          .filter((c) => selectedFolders.includes(c[0] as ModelFolder))
          .filter((c) => (c[1] ? !c[1].isDeployValid : null));

        if (this.state.loading) {
          return <Spinner />;
        }

        return (
          <>
            <div className="w-75 mx-auto mt-4">
              {activeModel.live && activeModel.lastDeployedAt && (
                <Alert variant="info">
                  This model was last deployed on{' '}
                  {`${moment(activeModel.lastDeployedAt).format(
                    'MMM DD, YYYY h:mma'
                  )}`}
                  {activeModel.lastDeployedBy &&
                    ` by ${activeModel.lastDeployedBy}`}
                </Alert>
              )}
              {(deploymentError || validationError) && (
                <Error message={validationError ?? deploymentError} />
              )}
              <div className="position-relative">
                <div
                  className={
                    activeModel.live ? 'pe-none opacity-50' : undefined
                  }
                >
                  {activeModel.type === 'customer_fit' ? (
                    <>
                      <StandardFitTab
                        openDeployPopup={this.openDeployPopup}
                        openImpactPopup={this.openImpactPopup}
                      />
                      <MultiFitTab
                        openDeployPopup={this.openDeployPopup}
                        openImpactPopup={this.openImpactPopup}
                      />
                    </>
                  ) : (
                    <>
                      <BehavioralTab
                        openDeployPopup={this.openDeployPopup}
                        openImpactPopup={this.openImpactPopup}
                      />
                      {activeModel.type === 'mqa' && (
                        <MultiEngagementTab
                          openDeployPopup={this.openDeployPopup}
                          openImpactPopup={this.openImpactPopup}
                        />
                      )}
                    </>
                  )}
                </div>
                {activeModel.live && (
                  <div
                    className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                    style={{ backgroundColor: 'rgba(255, 255, 255, 0.6)' }}
                  >
                    <Alert variant="info" className="text-center mx-3">
                      <strong>This model is already deployed.</strong>
                      <br />
                      To deploy again, undeploy the current model or duplicate
                      it.
                    </Alert>
                  </div>
                )}
              </div>
            </div>
            <Modal
              show={deployPopupIsShown}
              onHide={this.toggleShowDeployModal}
            >
              <Modal.Header>
                <Modal.Title>
                  Deploy{' '}
                  {activeModel.type === 'customer_fit' &&
                    upperFirst(selectedDeployMode)}{' '}
                  {MODEL_TYPES_NAMES[activeModel.type]} model?
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                {checkPermissions(
                  PROFILES_PERMISSIONS.ARCHITECT,
                  <>
                    {unconventionalCommits.map(([context, commit]) => (
                      <Alert variant="warning" key={`warning_${context}`}>
                        <strong>WARNING:</strong> The last modification to this
                        model{' '}
                        {isSuperKudu && (
                          <>
                            in <b>{context}</b>
                          </>
                        )}{' '}
                        was made outside of the Data Studio.{' '}
                        {isSuperKudu && (
                          <>
                            Changes made by {commit.author} at {commit.date}.
                          </>
                        )}
                        <br />
                        {isSuperKudu ? (
                          <a
                            target="_blank"
                            rel="noopener noreferrer"
                            href={`https://github.com/HGData/${TENANT_CONFIG_REPO}/tree/${TENANT_CONFIG_DEFAULT_BRANCH}/${getModelFilePath(
                              {
                                tenant,
                                modelType: activeModel.type,
                                modelId: activeModel.modelId,
                                folder: context as ModelFolder,
                                mode: selectedDeployMode,
                              }
                            )}`}
                          >
                            View the Github file
                          </a>
                        ) : (
                          <>
                            Please do not deploy and contact support@madkudu.com
                            to proceed to the deployment
                          </>
                        )}
                      </Alert>
                    ))}
                    {/* customer_fit only: it's the one type whose secondary
                        scoring tables are suffixed by the transformed model NAME
                        and whose realtime API returns a customer_fit_<name>
                        object. account_fit and mqa (engagement) use a single
                        wide table with columns suffixed by model_id and are
                        batch-only (no realtime object), so this warning is
                        inaccurate for them. */}
                    {selectedDeployMode !== 'standard' &&
                      isSuperKudu &&
                      activeModel.type === 'customer_fit' && (
                        <Alert variant="warning">
                          The model name will be transformed into{' '}
                          <b>{transformedModelName}</b>.
                          <ul>
                            <li>
                              - Redshift Tables will be suffixed with &apos;_
                              {transformedModelName}&apos;
                            </li>
                            <li>
                              - Realtime API will return a &apos;customer_fit_
                              {transformedModelName}&apos; object
                            </li>
                          </ul>
                          If you want to change it, go to the{' '}
                          <Link to={`/tenant/${tenant}`}>tenant page</Link> and
                          click on the &quot;Edit&quot; button next to the
                          model.
                        </Alert>
                      )}
                  </>
                )}
                <p>
                  {selectedDeployMode === 'standard' &&
                    'Any update made to this model will be deployed in production, and update scores within the next 8 hours in the integrations where a push configuration is enabled.'}
                  {selectedDeployMode === 'multi' &&
                    'This model will be deployed in parallel of any other Live Customer Fit model. To replace an existing Secondary Fit model, make sure the model names are the same, then deploy this model and undeploy the other model. Scores will update within the next 8 hours in the integrations where a push configuration is enabled. If you’d like to push a new model to new fields in your integrations please send a request to support@madkudu.com'}
                </p>
                {isDeploying && (
                  <>
                    <Spinner />
                    <div className="text-center">
                      Deploying your model, just a moment...
                    </div>
                  </>
                )}
              </Modal.Body>
              <Modal.Footer>
                <Button
                  onClick={this.toggleShowDeployModal}
                  disabled={isDeploying}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={() => this.handleDeployModel()}
                  disabled={
                    isDeploying ||
                    (!!unconventionalCommits.length && !isSuperKudu)
                  }
                >
                  {isDeploying ? 'Deploying...' : 'Deploy'}
                </Button>
              </Modal.Footer>
            </Modal>

            <Modal
              show={impactPopupIsShown}
              onHide={this.toggleShowImpactModal}
            >
              <Modal.Header>
                <Modal.Title>Initiate Model Analysis</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                {this.state.impactLaunching ? (
                  <div
                    className="text-center py-4"
                    data-testid="impact-analysis-loading"
                  >
                    <i
                      aria-hidden
                      className="fas fa-spinner fa-spin fa-2x mb-3"
                    />
                    <p className="mb-0">Starting the analysis…</p>
                  </div>
                ) : (
                  <>
                    <p>
                      By proceeding, you&apos;ll request a comparative analysis
                      for your model. Here&apos;s what to expect: A
                      comprehensive report for Model ID: {activeModel.modelId}{' '}
                      will be compiled. The results will be emailed to{' '}
                      {this.props.store.email}, typically within 3 hours. The
                      parameters passed will be:
                    </p>
                    <ul>
                      <li>Model ID: {activeModel.modelId}</li>
                      <li>Model Type: {activeModel.type}</li>
                      <li>Tenant: {tenant}</li>
                      <li>Email: {this.props.store.email}</li>
                    </ul>
                    <p>This will not affect your current live model.</p>
                    {this.state.impactError && (
                      <Alert variant="danger" className="mb-0">
                        {this.state.impactError}
                      </Alert>
                    )}
                  </>
                )}
              </Modal.Body>
              <Modal.Footer>
                <Button
                  variant="secondary"
                  onClick={this.toggleShowImpactModal}
                  disabled={this.state.impactLaunching}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={this.startImpactAnalysis}
                  disabled={this.state.impactLaunching}
                >
                  {this.state.impactLaunching
                    ? 'Starting analysis…'
                    : 'Start Analysis'}
                  {this.state.impactLaunching && (
                    <i aria-hidden className="fas fa-spinner fa-spin ms-1" />
                  )}
                </Button>
              </Modal.Footer>
            </Modal>
            <ToastContainer
              className="p-5"
              style={{ zIndex: 200 }}
              position="bottom-end"
            >
              <Toast
                show={this.state.impactStarted}
                onClose={() => this.setState({ impactStarted: false })}
                delay={10000}
                autohide
                bg="success"
              >
                <Toast.Header>
                  <strong className="me-auto">Analysis started</strong>
                </Toast.Header>
                <Toast.Body className="text-white">
                  Deployment impact analysis is running. Results will be emailed
                  to {this.props.store.email} within ~3 hours.
                </Toast.Body>
              </Toast>
            </ToastContainer>
          </>
        );
      }
    }
  )
);
